import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import { loadErrorMessage } from "shared/loadOrError";
import { canUseDataMapper } from "../rbac";
import { dataMapperSelection, dataMapperService, dataPlaneService } from "../dataMapperServices";
import { dialRequest, dataSourceErrorMessage } from "../Source/DataMapperSourceStore";
import { GONE_REASONS } from "../shared/editorSupport";
import { LatestIntentQueue } from "../shared/latestIntent";
import { DATA_MAPPER_ROUTE } from "../shared/editorRoutes";
import {
  isRecordChanged,
  refusalReason,
  sameId,
  visibleToSelection,
  type DataMapperProfile,
  type DataMapperScope,
  type DataMapperTargetConnection,
  type DataMapperTargetStatus,
  type DataMapperTargetTable,
} from "../dataMapper.types";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";

/** The Target and Import route names (RouterStore.goTo targets). */
export const DATA_MAPPER_TARGET_ROUTE = DATA_MAPPER_ROUTE.target;
export const DATA_MAPPER_IMPORT_ROUTE = DATA_MAPPER_ROUTE.import;

/** A target connection as the form edits it. The password is NOT here (see the class comment). */
export interface DataMapperTargetForm {
  name: string;
  server: string;
  port: string;
  database: string;
  authType: "sql" | "windows";
  username: string;
  /** Windows auth only: dialed with and saved with the target (not a secret, like the user name). */
  domain: string;
  encrypt: boolean;
  trustServerCertificate: boolean;
}

const EMPTY_FORM: DataMapperTargetForm = {
  name: "",
  server: "",
  port: "",
  database: "",
  authType: "sql",
  username: "",
  domain: "",
  encrypt: true,
  trustServerCertificate: false,
};

/** At most this many byte-pages of target tables are followed. */
const MAX_TABLE_PAGES = 20;

/** The sentence a 409 record-changed on a saved target shows. */
export const targetConnectionChangedNotice = (name: string): string =>
  `Someone else saved "${name}" after you loaded it, so your changes were not saved. ` +
  "The saved targets have been reloaded; load it again to apply yours.";

/**
 * Data Mapper → Target: the SQL Server database an import writes into. Test,
 * connect and save this tab's target (without its password), and list its
 * tables. Ported from the standalone tool's TargetConnectionStore / SqlTargetForm.
 *
 * CREDENTIALS — exactly the Data Source screen's rules. The password is never a
 * field of this store, of a saved connection or of anything persisted. The
 * component holds it in its own state and hands it to `test(password)` /
 * `connect(password)` (which are NOT MobX actions, so no spy() listener sees
 * it), they put it in the one call body (`dialRequest`) on the RAW Feathers
 * client, and the component clears it when the call settles (`dialOnce`).
 *
 * Saved targets are a computed view over the `data-mapper-target-connections`
 * ServiceStore (metadata only). The open target's tables are data-plane rows of
 * a client database, held here for the page's life like the Browse screen's.
 */
class DataMapperTargetStore {
  rootStore: RootStore;

  form: DataMapperTargetForm = { ...EMPTY_FORM };
  selectedConnectionId: string | null = null;
  selectedConnectionUpdatedAt: string | null = null;
  saveScope: DataMapperScope = "profile";
  private formProfileId: string | null = null;

  // This tab's target for this selection (the API's `status` answer).
  connected = false;
  connectedServer: string | null = null;
  connectedDatabase: string | null = null;
  statusLoaded = false;

  tables: DataMapperTargetTable[] = [];
  tablesLoading = false;
  tablesError: string | null = null;

  busy: "test" | "connect" | "disconnect" | "save" | null = null;
  testResult: string | null = null;
  error: string | null = null;
  notice: string | null = null;
  connectionsError: string | null = null;
  connectionsLoading = false;

  private loadSeq = 0;
  private opEpoch = 0;
  private tablesSeq = 0;
  /** connect / disconnect: one at a time, the latest intent wins (shared/latestIntent.ts). */
  private readonly intents = new LatestIntentQueue();

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    // The password-taking methods are not actions: MobX reports an action's
    // arguments to spy() listeners (devtools, loggers). Their state writes go
    // through runInAction, whose closures are not reported.
    makeAutoObservable(this, { rootStore: false, test: false, connect: false });
  }

  private get selection() {
    return dataMapperSelection(this.rootStore);
  }

  private get connectionsService() {
    return dataMapperService(this.rootStore, "data-mapper-target-connections");
  }

  private get service() {
    return dataPlaneService(this.rootStore, "data-mapper-target-connection");
  }

  get canUse(): boolean {
    return canUseDataMapper(this.rootStore);
  }

  get profileId(): string | null {
    return this.selection?.selectedProfileId ?? null;
  }

  get clientId(): string | null {
    return this.selection?.selectedClientId ?? null;
  }

  get profile(): DataMapperProfile | null {
    const id = this.profileId;
    if (!id) return null;
    const all = (dataMapperService(this.rootStore, "data-mapper-profiles")?.entities ??
      []) as unknown as DataMapperProfile[];
    return all.find((p) => sameId(p.id, id)) ?? null;
  }

  /** The saved targets this selection sees, narrowest scope first (the API's order). */
  get savedConnections(): DataMapperTargetConnection[] {
    if (!this.profileId) return [];
    const rows = (this.connectionsService?.entities ?? []) as unknown as DataMapperTargetConnection[];
    return rows.filter((r) => visibleToSelection(r, this.clientId, this.profileId));
  }

  get canDial(): boolean {
    return this.form.server.trim() !== "" && this.form.database.trim() !== "";
  }

  get canSave(): boolean {
    return this.canDial && this.busy === null;
  }

  /** "server / database" of the open target, for the header and the write confirmation. */
  get connectedLabel(): string | null {
    if (!this.connected) return null;
    return [this.connectedServer, this.connectedDatabase].filter(Boolean).join(" / ") || "a target";
  }

  // ---- Route entry ----------------------------------------------------------

  async enter(): Promise<void> {
    const authStore = (
      this.rootStore as unknown as { authStore?: { currentUser?: { id?: string | number } } }
    ).authStore;
    this.selection?.bindToUser(authStore?.currentUser?.id);
    if (!sameId(this.formProfileId, this.profileId)) {
      this.opEpoch++;
      this.intents.supersede();
      this.busy = null;
      this.form = { ...EMPTY_FORM };
      this.selectedConnectionId = null;
      this.selectedConnectionUpdatedAt = null;
      this.saveScope = "profile";
      this.setDisconnected();
      this.formProfileId = this.profileId;
    }
    await this.load();
  }

  async load(): Promise<void> {
    const seq = ++this.loadSeq;
    const selection = this.selection;
    if (!this.canUse || !selection?.hasProfile) {
      this.connectionsLoading = false;
      return;
    }
    const query = { ...selection.selectionQuery };
    this.connectionsLoading = true;
    this.connectionsError = null;
    // The reset after a sign-out bumps loadSeq, but the epoch moves first: a
    // settle in between (one find failed on its own, the others were ended)
    // would still pass the seq check.
    const ended = sessionMark(this.rootStore);
    const results = await Promise.allSettled([
      this.connectionsService
        ? this.connectionsService.find({ query })
        : Promise.reject(new Error("The Data Mapper service is not available in this build.")),
      this.refresh(),
      dataMapperService(this.rootStore, "data-mapper-profiles")?.find({ query: { clientId: query.clientId } }),
    ]);
    const failure = results.find((r): r is PromiseRejectedResult => r.status === "rejected");
    runInAction(() => {
      if (seq !== this.loadSeq || ended()) return;
      this.connectionsLoading = false;
      if (!failure || isSessionEnded(failure.reason)) return;
      const err: unknown = failure.reason;
      if (GONE_REASONS.has(refusalReason(err) ?? "") && query.profileId) {
        selection.forgetProfile(query.profileId);
        this.connectionsError = "The profile that was open no longer exists. Choose one on the Profiles page.";
      } else {
        this.connectionsError = loadErrorMessage(err, "The saved targets could not be loaded.");
      }
    });
  }

  /** Ask the API whether this tab has a target open for this selection; list its tables if so. */
  async refresh(): Promise<void> {
    const selection = this.selection;
    const service = this.service;
    if (!selection?.hasProfile || !service) return;
    const seq = this.loadSeq;
    const ended = sessionMark(this.rootStore);
    const answer = (await service.create({ action: "status", ...selection.dataPlaneQuery })) as
      | Partial<DataMapperTargetStatus>
      | null;
    // Past a sign-out: no status latched and no table list started for the next person.
    if (seq !== this.loadSeq || ended()) return;
    const connected = answer?.connected === true;
    runInAction(() => {
      if (seq !== this.loadSeq) return;
      this.connected = connected;
      this.connectedServer = connected ? (answer?.server ?? null) : null;
      this.connectedDatabase = connected ? (answer?.database ?? null) : null;
      this.statusLoaded = true;
      if (!connected) this.tables = [];
    });
    if (connected) await this.listTables();
  }

  private setDisconnected() {
    this.connected = false;
    this.connectedServer = null;
    this.connectedDatabase = null;
    this.tables = [];
    this.tablesError = null;
    // A listing still in flight belongs to the target that just closed.
    this.tablesSeq++;
    this.tablesLoading = false;
  }

  // ---- Form ------------------------------------------------------------------

  /** Frozen while a dial or save is in flight: its answer belongs to the form as it was. */
  setFormField<K extends keyof DataMapperTargetForm>(field: K, value: DataMapperTargetForm[K]) {
    if (this.busy) return;
    this.form = { ...this.form, [field]: value };
    this.testResult = null;
  }

  setSaveScope(scope: DataMapperScope) {
    this.saveScope = scope;
  }

  /** Fill the form from a saved target. A saved target never has a password; the person types it. */
  loadConnection(id: string | null) {
    if (this.busy) return;
    this.error = null;
    this.notice = null;
    this.testResult = null;
    if (!id) {
      this.selectedConnectionId = null;
      this.selectedConnectionUpdatedAt = null;
      this.form = { ...EMPTY_FORM };
      return;
    }
    const row = this.savedConnections.find((c) => sameId(c.id, id));
    if (!row) return;
    this.selectedConnectionId = row.id;
    this.selectedConnectionUpdatedAt = row.updatedAt;
    this.saveScope = row.scope;
    this.form = {
      name: row.name,
      server: row.server,
      port: row.port == null ? "" : String(row.port),
      database: row.database,
      authType: row.authType === "windows" ? "windows" : "sql",
      username: row.username ?? "",
      domain: row.domain ?? "",
      encrypt: row.encrypt !== false,
      trustServerCertificate: row.trustServerCertificate === true,
    };
  }

  /** The form as a dial config, WITHOUT a password. */
  private configBody(): Record<string, unknown> {
    const f = this.form;
    const body: Record<string, unknown> = {
      type: "mssql",
      server: f.server.trim(),
      database: f.database.trim(),
      authType: f.authType,
      encrypt: f.encrypt,
      trustServerCertificate: f.trustServerCertificate,
    };
    if (f.username.trim()) body.username = f.username.trim();
    // The API requires a domain for Windows auth (mssql-adapter.ts parseMssqlConfig).
    if (f.authType === "windows" && f.domain.trim()) body.domain = f.domain.trim();
    const port = Number(f.port);
    if (f.port.trim() !== "" && Number.isInteger(port)) body.port = port;
    return body;
  }

  /**
   * Start a dial / save: the token it returns says whether the call is still the
   * live one. It is not once the route moved on (`opEpoch`), nor once a sign-out
   * has ended the session — the epoch moves before `resetForSignOut` runs, and
   * the library ServiceStore `connectionsService.patch` resolves instead of
   * throwing then.
   */
  private begin(kind: NonNullable<DataMapperTargetStore["busy"]>): () => boolean {
    this.busy = kind;
    this.error = null;
    this.testResult = null;
    const epoch = this.opEpoch;
    const ended = sessionMark(this.rootStore);
    return () => epoch === this.opEpoch && !ended();
  }

  private settle(live: () => boolean, apply: () => void) {
    runInAction(() => {
      if (!live()) return;
      apply();
      this.busy = null;
    });
  }

  // ---- Dials (take the password; not MobX actions) ---------------------------

  /** Test the target (opens and closes a pool; this tab's target is unchanged). */
  async test(password: string): Promise<boolean> {
    const service = this.service;
    const query = this.selection?.dataPlaneQuery;
    if (!service || !this.canDial || !this.selection?.hasProfile || !query || this.busy) return false;
    const config = this.configBody();
    const live = this.begin("test");
    try {
      await service.create(dialRequest("test", query, config, password));
      this.settle(live, () => {
        this.testResult = "The target connection works.";
      });
      return true;
    } catch (err) {
      if (isSessionEnded(err)) return false;
      this.settle(live, () => {
        this.error = dataSourceErrorMessage(err, "The connection test failed.");
      });
      return false;
    }
  }

  /**
   * Busy with something other than connect / disconnect (a test, a save): those
   * two queue behind each other, but not behind these.
   */
  get busyOtherThanDial(): boolean {
    return this.busy !== null && this.busy !== "connect" && this.busy !== "disconnect";
  }

  /**
   * Open this tab's target, then list its tables. Asked while a connect or
   * disconnect is in flight, it waits for it; asked again before it starts,
   * only the latest of the waiting calls runs (it answers false when skipped).
   */
  async connect(password: string): Promise<boolean> {
    const service = this.service;
    const query = this.selection?.dataPlaneQuery;
    if (!service || !this.canDial || !this.selection?.hasProfile || !query || this.busyOtherThanDial) return false;
    // The form as it is now: the intent is what was on screen when Connect was clicked.
    const config = this.configBody();
    const outcome = await this.intents.run(async () => {
      const live = this.begin("connect");
      let connected = false;
      try {
        const answer = (await service.create(dialRequest("connect", query, config, password))) as
          | Partial<DataMapperTargetStatus>
          | null;
        connected = answer?.connected === true;
        this.settle(live, () => {
          this.connected = connected;
          this.connectedServer = connected ? (answer?.server ?? null) : null;
          this.connectedDatabase = connected ? (answer?.database ?? null) : null;
          this.statusLoaded = true;
        });
      } catch (err) {
        if (isSessionEnded(err)) return false;
        this.settle(live, () => {
          this.error = dataSourceErrorMessage(err, "The connection failed.");
        });
        return false;
      }
      return connected && live();
    });
    // The listing runs outside the queue slot: a disconnect asked meanwhile does not wait
    // behind up to MAX_TABLE_PAGES of it, and drops its answer (setDisconnected, Codex r1).
    if (outcome.ran && outcome.value) await this.listTables();
    return outcome.ran ? outcome.value : false;
  }

  /** Close this tab's target. Serialised with connect, latest intent wins. */
  async disconnect(): Promise<void> {
    const service = this.service;
    const query = this.selection?.dataPlaneQuery;
    if (!service || !this.selection?.hasProfile || !query || this.busyOtherThanDial) return;
    await this.intents.run(async () => {
      const live = this.begin("disconnect");
      try {
        await service.create({ action: "disconnect", ...query });
        this.settle(live, () => this.setDisconnected());
      } catch (err) {
        if (isSessionEnded(err)) return;
        this.settle(live, () => {
          this.error = dataSourceErrorMessage(err, "The target could not be closed.");
        });
      }
    });
  }

  /** The open target's tables (names and row counts), following the byte-paged cursor. */
  async listTables(): Promise<void> {
    const service = this.service;
    const query = this.selection?.dataPlaneQuery;
    if (!service || !query) return;
    const seq = ++this.tablesSeq;
    const ended = sessionMark(this.rootStore);
    const current = () => seq === this.tablesSeq && !ended();
    this.tablesLoading = true;
    this.tablesError = null;
    try {
      const rows: DataMapperTargetTable[] = [];
      let skip: number | null = 0;
      // shortcut: at most MAX_TABLE_PAGES byte-pages (each ~1 MB of names); a bigger catalogue is cut and says so.
      for (let page = 0; skip !== null && page < MAX_TABLE_PAGES; page++) {
        // A disconnect, a newer listing or a sign-out while a page was in flight:
        // do not ask for the next page of a catalogue nobody is looking at.
        if (!current()) return;
        const answer = (await service.create({ action: "list-tables", ...query, $skip: skip })) as {
          rows?: DataMapperTargetTable[];
          nextCursor?: number | null;
        } | null;
        rows.push(...(answer?.rows ?? []));
        skip = typeof answer?.nextCursor === "number" && answer.nextCursor > skip ? answer.nextCursor : null;
      }
      const cut = skip !== null;
      runInAction(() => {
        if (!current()) return;
        this.tables = rows;
        this.tablesLoading = false;
        if (cut) this.tablesError = `The target has more tables than HQ lists here; the first ${rows.length} are shown.`;
      });
    } catch (err) {
      if (isSessionEnded(err)) return;
      runInAction(() => {
        if (!current()) return;
        this.tablesLoading = false;
        this.tablesError = dataSourceErrorMessage(err, "The target's tables could not be listed.");
      });
    }
  }

  // ---- Saved targets ---------------------------------------------------------

  /**
   * Save the form as a target connection: a patch of the loaded one (with
   * expectUpdatedAt) or, with `asNew` or nothing loaded, a new one in
   * `saveScope`. Never carries a password.
   */
  async saveConnection(asNew = false): Promise<boolean> {
    const service = this.connectionsService;
    const selection = this.selection;
    if (!service || !selection?.hasProfile || !this.canDial || this.busy) return false;
    const f = this.form;
    const name = f.name.trim() || f.database.trim();
    const port = Number(f.port);
    const fields = {
      name,
      server: f.server.trim(),
      database: f.database.trim(),
      authType: f.authType,
      username: f.username.trim() || null,
      // Windows auth's domain is saved like the user name; SQL auth has none (null clears an old one).
      domain: f.authType === "windows" && f.domain.trim() ? f.domain.trim() : null,
      port: f.port.trim() !== "" && Number.isInteger(port) ? port : null,
      encrypt: f.encrypt,
      trustServerCertificate: f.trustServerCertificate,
    };
    const editingId = asNew ? null : this.selectedConnectionId;
    const selectionQuery = { ...selection.selectionQuery };
    const live = this.begin("save");
    this.notice = null;
    try {
      const saved = (
        editingId
          ? await service.patch(
              editingId,
              {
                ...fields,
                ...(this.selectedConnectionUpdatedAt ? { expectUpdatedAt: this.selectedConnectionUpdatedAt } : {}),
              } as never,
              { query: selectionQuery } as never
            )
          : await service.create({ ...fields, scope: this.saveScope, ...selectionQuery } as never)
      ) as DataMapperTargetConnection | null;
      this.settle(live, () => {
        if (saved?.id) {
          this.selectedConnectionId = saved.id;
          this.selectedConnectionUpdatedAt = saved.updatedAt;
        }
        this.notice = `Saved "${name}". The password is not saved; type it each time you connect.`;
      });
      return true;
    } catch (err) {
      if (isSessionEnded(err)) return false;
      if (isRecordChanged(err)) {
        this.settle(live, () => {
          this.notice = targetConnectionChangedNotice(name);
          this.selectedConnectionId = null;
          this.selectedConnectionUpdatedAt = null;
        });
        if (live()) await this.load();
        return false;
      }
      this.settle(live, () => {
        this.error = loadErrorMessage(err, "The target could not be saved.");
      });
      return false;
    }
  }

  clearError() {
    this.error = null;
  }

  clearNotice() {
    this.notice = null;
  }

  goToImport() {
    void this.rootStore.routerStore?.goTo(DATA_MAPPER_IMPORT_ROUTE);
  }

  /** Route exit: drop messages. The form, the chosen target and the table list stay for the Import screen. */
  reset() {
    this.loadSeq++;
    this.opEpoch++;
    this.intents.supersede();
    this.busy = null;
    this.error = null;
    this.notice = null;
    this.testResult = null;
    this.connectionsError = null;
    this.connectionsLoading = false;
  }

  /**
   * Everything reset() keeps across this wizard's route changes goes too: on
   * sign-out it was the previous person's. Called by RootStore.purgeUserData,
   * which prefers this to reset().
   */
  resetForSignOut() {
    this.reset();
    this.form = { ...EMPTY_FORM };
    this.selectedConnectionId = null;
    this.selectedConnectionUpdatedAt = null;
    this.saveScope = "profile";
    this.formProfileId = null;
    this.connected = false;
    this.connectedServer = null;
    this.connectedDatabase = null;
    this.statusLoaded = false;
    // A listing still in flight was the previous person's: its answer must
    // find the sequence moved on, as setDisconnected() does for a closed target.
    this.tablesSeq++;
    this.tables = [];
    this.tablesLoading = false;
    this.tablesError = null;
  }
}

export default DataMapperTargetStore;
